import {
  CONFIDENCE_FILTERING_ENABLED,
  DEFAULT_CONFIDENCE_THRESHOLD,
} from "../config/gridLinesConfig";
import { useState, useRef, useEffect, useMemo } from "react";

/**
 * Comprehensive state management hook for PdfTableViewer
 * Consolidates all useState declarations organized by functional area
 */
const usePdfTableState = () => {
  // ============================================================================
  // PDF DOCUMENT STATE
  // ============================================================================
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(0.8);
  const [highlights, setHighlights] = useState([]);
  const [selectedWordIndex, setSelectedWordIndex] = useState(null);

  // ============================================================================
  // TABLE DATA STATE
  // ============================================================================
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null); // Store modifiable table data
  const [selectedCell, setSelectedCell] = useState(null);
  const [cachedTableData, setCachedTableData] = useState({}); // Cache modified table data per table ID

  // ============================================================================
  // UI CONTROL STATE
  // ============================================================================
  const [pdfPanelExpanded, setPdfPanelExpanded] = useState(true); // Control PDF panel visibility
  const [sidebarWidth, setSidebarWidth] = useState(50); // Sidebar width as percentage (50% default)
  const [isResizing, setIsResizing] = useState(false); // Track if user is dragging
  const [showStats, setShowStats] = useState(false); // Display column stats
  const [showGrid, setShowGrid] = useState(true); // Show/hide grid lines
  const [showConfidence, setShowConfidence] = useState(true); // Show confidence highlighting
  const [tableZoom, setTableZoom] = useState(0.8); // Table zoom level (0.8 = 80%)
  const [isProcessingContinue, setIsProcessingContinue] = useState(false); // For loading overlay
  const [isInitialPdfRenderComplete, setIsInitialPdfRenderComplete] = useState(
    false
  ); // PDF loading state
  const [confidenceFilterEnabled, setConfidenceFilterEnabled] = useState(
    CONFIDENCE_FILTERING_ENABLED
  ); // Enable/disable confidence filtering
  const [confidenceThreshold, setConfidenceThreshold] = useState(
    DEFAULT_CONFIDENCE_THRESHOLD
  ); // Minimum confidence threshold (0-1)

  // ============================================================================
  // MODAL AND DIALOG STATE
  // ============================================================================
  const [showGridLineConfirmation, setShowGridLineConfirmation] = useState(
    false
  );
  const [showCellDetail, setShowCellDetail] = useState(false);
  const [selectedCellDetail, setSelectedCellDetail] = useState(null);
  const [showRecoveryMenu, setShowRecoveryMenu] = useState(false);
  const [recoveryMenuAnchor, setRecoveryMenuAnchor] = useState(null);
  const [cellInfoAnchor, setCellInfoAnchor] = useState(null);
  const [showDrawingConfirmation, setShowDrawingConfirmation] = useState(false);

  // ============================================================================
  // GRID LINE STATE
  // ============================================================================
  const [dynamicGridLines, setDynamicGridLines] = useState({});
  const [gridLineDragMode, setGridLineDragMode] = useState(true); // Enable automatic drag mode by default
  const [isDraggingGridLine, setIsDraggingGridLine] = useState(false);
  const [draggingGridLine, setDraggingGridLine] = useState(null);
  const [gridLineStartPos, setGridLineStartPos] = useState(null);
  const [pendingGridLineChanges, setPendingGridLineChanges] = useState(null);
  const [originalGridLines, setOriginalGridLines] = useState(null);
  const [isPlacingGridLine, setIsPlacingGridLine] = useState(false);
  const [placingGridLineType, setPlacingGridLineType] = useState(null);
  const [placingGridLinePosition, setPlacingGridLinePosition] = useState(null);
  const [isGridLineInPreview, setIsGridLineInPreview] = useState(false);
  const [persistentGridLineTooltip, setPersistentGridLineTooltip] = useState(
    null
  );
  const [showGridLineMoved, setShowGridLineMoved] = useState(false);
  const [pendingNewGridLines, setPendingNewGridLines] = useState([]);

  // ============================================================================
  // DRAWING TOOLS STATE
  // ============================================================================
  const [drawingMode, setDrawingMode] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingShape, setDrawingShape] = useState(null);
  const [previewWords, setPreviewWords] = useState([]);
  const [pendingShapeWords, setPendingShapeWords] = useState([]);
  const [drawingStartPoint, setDrawingStartPoint] = useState(null);
  const [polygonPoints, setPolygonPoints] = useState([]);

  // ============================================================================
  // CELL EDITING STATE
  // ============================================================================
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  // ============================================================================
  // ID SYSTEM STATE
  // ============================================================================
  const [columnIds, setColumnIds] = useState([]);
  const [rowIds, setRowIds] = useState([]);
  const [deletedColumns, setDeletedColumns] = useState(new Map());
  const [deletedRows, setDeletedRows] = useState(new Map());
  const [originalTableStructure, setOriginalTableStructure] = useState(null);

  // ============================================================================
  // REFS
  // ============================================================================
  const canvasRef = useRef(null);
  const highlightContainerRef = useRef(null);
  const pdfPageDimensions = useRef({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const beforeTableRef = useRef(null);
  const afterTableRef = useRef(null);

  // ============================================================================
  // AUTOMATIC GRID LINE DRAG MODE MANAGEMENT
  // ============================================================================
  useEffect(() => {
    // Disable grid line dragging when drawing tools are active or placing grid lines
    if (drawingMode || isPlacingGridLine) {
      // Disable grid line dragging when drawing tools are active or placing grid lines
      setGridLineDragMode(false);
    } else {
      // Enable grid line dragging when no tools are active
      setGridLineDragMode(true);
    }
  }, [drawingMode, isPlacingGridLine]);

  // ============================================================================
  // RETURN ORGANIZED STATE OBJECT (MEMOIZED TO PREVENT RE-RENDERS)
  // ============================================================================

  // Memoize state objects to prevent unnecessary re-renders
  const pdfState = useMemo(
    () => ({
      pdfDoc,
      setPdfDoc,
      totalPages,
      setTotalPages,
      currentPage,
      setCurrentPage,
      pdfScale,
      setPdfScale,
      highlights,
      setHighlights,
      selectedWordIndex,
      setSelectedWordIndex,
    }),
    [pdfDoc, totalPages, currentPage, pdfScale, highlights, selectedWordIndex]
  );

  const tableState = useMemo(
    () => ({
      selectedTable,
      setSelectedTable,
      tableData,
      setTableData,
      selectedCell,
      setSelectedCell,
      cachedTableData,
      setCachedTableData,
    }),
    [selectedTable, tableData, selectedCell, cachedTableData]
  );

  const uiState = useMemo(
    () => ({
      pdfPanelExpanded,
      setPdfPanelExpanded,
      sidebarWidth,
      setSidebarWidth,
      isResizing,
      setIsResizing,
      showStats,
      setShowStats,
      showGrid,
      setShowGrid,
      showConfidence,
      setShowConfidence,
      tableZoom,
      setTableZoom,
      isProcessingContinue,
      setIsProcessingContinue,
      isInitialPdfRenderComplete,
      setIsInitialPdfRenderComplete,
      confidenceFilterEnabled,
      setConfidenceFilterEnabled,
      confidenceThreshold,
      setConfidenceThreshold,
    }),
    [
      pdfPanelExpanded,
      sidebarWidth,
      isResizing,
      showStats,
      showGrid,
      showConfidence,
      tableZoom,
      isProcessingContinue,
      isInitialPdfRenderComplete,
      confidenceFilterEnabled,
      confidenceThreshold,
    ]
  );

  const modalState = useMemo(
    () => ({
      showGridLineConfirmation,
      setShowGridLineConfirmation,
      showCellDetail,
      setShowCellDetail,
      selectedCellDetail,
      setSelectedCellDetail,
      showRecoveryMenu,
      setShowRecoveryMenu,
      recoveryMenuAnchor,
      setRecoveryMenuAnchor,
      cellInfoAnchor,
      setCellInfoAnchor,
      showDrawingConfirmation,
      setShowDrawingConfirmation,
    }),
    [
      showGridLineConfirmation,
      showCellDetail,
      selectedCellDetail,
      showRecoveryMenu,
      recoveryMenuAnchor,
      cellInfoAnchor,
      showDrawingConfirmation,
    ]
  );

  const gridLineState = useMemo(
    () => ({
      dynamicGridLines,
      setDynamicGridLines,
      gridLineDragMode,
      setGridLineDragMode,
      isDraggingGridLine,
      setIsDraggingGridLine,
      draggingGridLine,
      setDraggingGridLine,
      gridLineStartPos,
      setGridLineStartPos,
      pendingGridLineChanges,
      setPendingGridLineChanges,
      originalGridLines,
      setOriginalGridLines,
      isPlacingGridLine,
      setIsPlacingGridLine,
      placingGridLineType,
      setPlacingGridLineType,
      placingGridLinePosition,
      setPlacingGridLinePosition,
      isGridLineInPreview,
      setIsGridLineInPreview,
      persistentGridLineTooltip,
      setPersistentGridLineTooltip,
      showGridLineMoved,
      setShowGridLineMoved,
      pendingNewGridLines,
      setPendingNewGridLines,
    }),
    [
      dynamicGridLines,
      gridLineDragMode,
      isDraggingGridLine,
      draggingGridLine,
      gridLineStartPos,
      pendingGridLineChanges,
      originalGridLines,
      isPlacingGridLine,
      placingGridLineType,
      placingGridLinePosition,
      isGridLineInPreview,
      persistentGridLineTooltip,
      showGridLineMoved,
      pendingNewGridLines,
    ]
  );

  const drawingState = useMemo(
    () => ({
      drawingMode,
      setDrawingMode,
      isDrawing,
      setIsDrawing,
      drawingShape,
      setDrawingShape,
      previewWords,
      setPreviewWords,
      pendingShapeWords,
      setPendingShapeWords,
      drawingStartPoint,
      setDrawingStartPoint,
      polygonPoints,
      setPolygonPoints,
    }),
    [
      drawingMode,
      isDrawing,
      drawingShape,
      previewWords,
      pendingShapeWords,
      drawingStartPoint,
      polygonPoints,
    ]
  );

  const editingState = useMemo(
    () => ({
      editingCell,
      setEditingCell,
      editValue,
      setEditValue,
    }),
    [editingCell, editValue]
  );

  const idSystemState = useMemo(
    () => ({
      columnIds,
      setColumnIds,
      rowIds,
      setRowIds,
      deletedColumns,
      setDeletedColumns,
      deletedRows,
      setDeletedRows,
      originalTableStructure,
      setOriginalTableStructure,
    }),
    [columnIds, rowIds, deletedColumns, deletedRows, originalTableStructure]
  );

  const refs = useMemo(
    () => ({
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      containerRef,
      beforeTableRef,
      afterTableRef,
    }),
    [
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      containerRef,
      beforeTableRef,
      afterTableRef,
    ]
  );

  return {
    // PDF Document State
    pdfState,

    // Table Data State
    tableState,

    // UI Control State
    uiState,

    // Modal and Dialog State
    modalState,

    // Grid Line State
    gridLineState,

    // Drawing Tools State
    drawingState,

    // Cell Editing State
    editingState,

    // ID System State
    idSystemState,

    // Refs
    refs,
  };
};

export default usePdfTableState;
